import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { apiFetch } from '@/lib/query-client';

// Búsqueda global Cmd+K. Debounce 300ms en el caller, este hook recibe la
// query ya debounceada. staleTime corto (5s) porque los datos cambian
// rápido, pero cacheamos para que reabrir el palette no re-pegue siempre.

const itemSchema = z.object({
  id: z.string(),
  type: z.enum(['client', 'lead', 'task', 'invoice', 'contract', 'quotation']),
  title: z.string(),
  subtitle: z.string().nullable(),
  url: z.string(),
});

const resultsSchema = z.object({
  clients: z.array(itemSchema),
  leads: z.array(itemSchema),
  tasks: z.array(itemSchema),
  invoices: z.array(itemSchema),
  contracts: z.array(itemSchema),
  quotations: z.array(itemSchema),
});

export type GlobalSearchItem = z.infer<typeof itemSchema>;
export type GlobalSearchResults = z.infer<typeof resultsSchema>;

const EMPTY: GlobalSearchResults = {
  clients: [],
  leads: [],
  tasks: [],
  invoices: [],
  contracts: [],
  quotations: [],
};

export function useGlobalSearch(query: string) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 1;
  return useQuery<GlobalSearchResults>({
    queryKey: ['global-search', trimmed],
    enabled,
    staleTime: 5_000,
    queryFn: async () => {
      const raw = await apiFetch<unknown>({
        path: `/v1/search?q=${encodeURIComponent(trimmed)}`,
      });
      return resultsSchema.parse(raw);
    },
    placeholderData: (prev) => prev ?? EMPTY,
  });
}

// Debounce simple para acomodar el patron "user escribe rápido".
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}
